import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
    Loader2, ClipboardList, RefreshCw, User, Calendar, Activity,
    ChevronLeft, ChevronRight, MapPin, FileText, Shield, AlertCircle
} from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const getErrorMessage = (err) => {
    try {
        const detail = err?.response?.data?.detail;
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail)) return detail.map(e => e?.msg || 'Error').join('. ');
        return err?.message || 'An error occurred';
    } catch { return 'An error occurred'; }
};

const AdminAudit = () => {
    const [loading, setLoading] = useState(true);
    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, total_pages: 0 });
    const [actionFilter, setActionFilter] = useState('all');
    const [actionTypes, setActionTypes] = useState([]);

    const loadLogs = useCallback(async (page = 1, filter = actionFilter) => {
        setLoading(true);
        try {
            const res = await adminAPI.getAuditLogsPaginated(page, pagination.limit, filter);
            setLogs(res.data.logs || []);
            setPagination(res.data.pagination);
            
            // Extract unique action types for filter
            if (page === 1 && filter === 'all') {
                const types = [...new Set(res.data.logs.map(l => l.action).filter(Boolean))];
                setActionTypes(types);
            }
        } catch (err) {
            toast.error(getErrorMessage(err));
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [actionFilter, pagination.limit]);

    useEffect(() => { loadLogs(); }, []);

    const handleFilterChange = (value) => {
        setActionFilter(value);
        loadLogs(1, value);
    };

    const getActionBadgeColor = (action) => {
        if (action?.includes('login')) return 'bg-blue-600';
        if (action?.includes('deactivat') || action?.includes('suspend')) return 'bg-amber-600';
        if (action?.includes('delete') || action?.includes('reject') || action?.includes('strike_added')) return 'bg-red-600';
        if (action?.includes('approv') || action?.includes('reactivat') || action?.includes('strike_removed')) return 'bg-emerald-600';
        if (action?.includes('reset') || action?.includes('evidence')) return 'bg-purple-600';
        if (action?.includes('hidden')) return 'bg-gray-600';
        return 'bg-slate-600';
    };

    const getActionIcon = (action) => {
        if (action?.includes('review')) return <FileText className="h-4 w-4" />;
        if (action?.includes('user') || action?.includes('login')) return <User className="h-4 w-4" />;
        if (action?.includes('strike')) return <AlertCircle className="h-4 w-4" />;
        if (action?.includes('ownership')) return <MapPin className="h-4 w-4" />;
        return <Activity className="h-4 w-4" />;
    };

    const formatActionName = (action) => {
        return (action || 'unknown')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, l => l.toUpperCase());
    };

    if (loading && logs.length === 0) {
        return (
            <AdminLayout>
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="p-6 lg:p-8 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="font-heading font-extrabold text-2xl lg:text-3xl text-foreground">Audit Log</h1>
                        <p className="text-muted-foreground">Platform activity and admin actions ({pagination.total} records)</p>
                    </div>
                    <Button onClick={() => loadLogs(pagination.page)} variant="outline" size="sm" disabled={loading}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                </div>

                {/* Filter */}
                <Card className="bg-card border-border">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-muted-foreground">Filter by action:</span>
                            <Select value={actionFilter} onValueChange={handleFilterChange}>
                                <SelectTrigger className="w-56 bg-accent border-border">
                                    <SelectValue placeholder="All actions" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Actions</SelectItem>
                                    {actionTypes.map((action) => (
                                        <SelectItem key={action} value={action}>
                                            {formatActionName(action)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Logs List */}
                <Card className="bg-card border-border">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading font-bold text-lg flex items-center gap-2">
                            <ClipboardList className="h-5 w-5 text-primary" />
                            Activity Log
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {logs.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                <p className="text-lg font-medium">No logs found</p>
                                <p className="text-sm">Activity will appear here as actions are performed</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {logs.map((log, index) => (
                                    <div key={log.id || index} className="p-4 bg-accent/30 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                                <div className="p-2 rounded-lg bg-background">
                                                    {getActionIcon(log.action)}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                                        <Badge className={getActionBadgeColor(log.action)}>
                                                            {formatActionName(log.action)}
                                                        </Badge>
                                                    </div>
                                                    
                                                    {/* Action Details */}
                                                    <div className="space-y-1 text-sm">
                                                        {/* Admin who performed action */}
                                                        {(log.admin_email || log.admin_name) && (
                                                            <p className="flex items-center gap-1 text-muted-foreground">
                                                                <Shield className="h-3 w-3" />
                                                                Admin: <span className="text-foreground">{log.admin_email || log.admin_name}</span>
                                                            </p>
                                                        )}
                                                        
                                                        {/* Target user */}
                                                        {log.target_email && (
                                                            <p className="flex items-center gap-1 text-muted-foreground">
                                                                <User className="h-3 w-3" />
                                                                User: <span className="text-foreground">{log.target_email}</span>
                                                            </p>
                                                        )}
                                                        
                                                        {/* Review ID */}
                                                        {log.review_id && (
                                                            <p className="flex items-center gap-1 text-muted-foreground">
                                                                <FileText className="h-3 w-3" />
                                                                Review: <code className="text-xs bg-muted px-1 rounded">{log.review_id.slice(0, 12)}...</code>
                                                            </p>
                                                        )}
                                                        
                                                        {/* Property Address */}
                                                        {log.property_address && (
                                                            <p className="flex items-center gap-1 text-muted-foreground">
                                                                <MapPin className="h-3 w-3" />
                                                                Property: <span className="text-foreground">{log.property_address}</span>
                                                            </p>
                                                        )}
                                                        
                                                        {/* Strike info */}
                                                        {(log.previous_strikes !== undefined || log.new_strikes !== undefined) && (
                                                            <p className="text-muted-foreground">
                                                                Strikes: {log.previous_strikes} → {log.new_strikes}
                                                            </p>
                                                        )}
                                                        
                                                        {/* Reason/Notes */}
                                                        {log.reason && (
                                                            <p className="text-muted-foreground">
                                                                Reason: <span className="text-foreground italic">"{log.reason}"</span>
                                                            </p>
                                                        )}
                                                        {log.notes && (
                                                            <p className="text-muted-foreground">
                                                                Notes: <span className="text-foreground italic">"{log.notes}"</span>
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Timestamp */}
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                                                <Calendar className="h-3 w-3" />
                                                {formatDate(log.timestamp || log.created_at)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.total_pages > 1 && (
                            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                                <p className="text-sm text-muted-foreground">
                                    Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
                                </p>
                                <div className="flex gap-2">
                                    <Button 
                                        size="sm" 
                                        variant="outline" 
                                        disabled={pagination.page <= 1 || loading}
                                        onClick={() => loadLogs(pagination.page - 1)}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                        size="sm" 
                                        variant="outline"
                                        disabled={pagination.page >= pagination.total_pages || loading}
                                        onClick={() => loadLogs(pagination.page + 1)}
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    );
};

export default AdminAudit;
